// V25.7 — Data Completeness banner.
//
// Amber, non-blocking. Names any month at the tail of the reporting window
// that QuickBooks returned empty or near-empty, and offers a one-click jump
// to the last fully-posted month-end.

import React from 'react';
import { CalendarClock, ArrowRight } from 'lucide-react';
import { formatDateLabel } from '../../lib/monthCompleteness';

export default function DataCompletenessBanner({ analysis, onUsePeriodEnd = null, busy = false }) {
  if (!analysis?.hasIssues) return null;
  const { trailingIssues, lastFullyPosted } = analysis;

  return (
    <div
      data-testid="data-completeness-banner"
      style={{
        margin: '12px 0',
        padding: '14px 18px',
        background: '#FFFBEB',
        border: '1px solid #FCD34D',
        borderRadius: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <CalendarClock size={17} color="#B45309" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#78350F', marginBottom: 6 }}>
            {trailingIssues.length === 1
              ? '1 month in this period has little or no data in QuickBooks'
              : `${trailingIssues.length} months in this period have little or no data in QuickBooks`}
          </div>
          <ul style={{ margin: '0 0 8px', paddingLeft: 18, fontSize: 13, color: '#92400E', lineHeight: 1.7 }}>
            {trailingIssues.map((m) => (
              <li key={m.shortLabel} data-testid={`data-completeness-month-${m.shortLabel.replace(' ', '-')}`}>
                <strong>{m.label}</strong> — {m.note}
              </li>
            ))}
          </ul>
          <div style={{ fontSize: 12, color: '#92400E' }}>
            These months will print as blank columns. The statements are reporting
            QuickBooks accurately — the write-up for {trailingIssues.length === 1
              ? 'this month'
              : 'these months'} hasn&apos;t been posted yet.
          </div>
          {lastFullyPosted && onUsePeriodEnd && (
            <button
              data-testid="use-last-posted-month-btn"
              disabled={busy}
              onClick={() => onUsePeriodEnd(lastFullyPosted.end)}
              style={{
                marginTop: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 14px',
                borderRadius: 7,
                border: 'none',
                background: busy ? '#D6D3D1' : '#B45309',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                cursor: busy ? 'default' : 'pointer',
                transition: 'background-color 160ms ease',
              }}
            >
              {busy ? 'Reloading…' : `Use ${formatDateLabel(lastFullyPosted.end)} instead`}
              {!busy && <ArrowRight size={13} />}
            </button>
          )}
          {lastFullyPosted && (
            <div style={{ fontSize: 11, color: '#A16207', marginTop: 6 }}>
              Last fully-posted month: {lastFullyPosted.label}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
